import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import {
  compactTime,
  formatDuration,
  summariseDay,
  type CalendarBlock,
  type CalendarDueItem,
  type ISODate,
  type Task,
} from '@app/shared'

import { prefersReducedMotion } from '../../lib/motion'
import { countdownPhrase, minutesUntil, taskLine } from './derive'
import { Grip } from './icons'
import { LANE_BAR, LANE_DOT } from './palette'
import { anchorOfElement, type PeekAnchor } from '../../lib/popover'

/**
 * 13d · the travelling drag chip: tilted `-1.5°`, carrying an `0 8px 20px` shadow.
 *
 * "Enough to feel lifted" — and nothing more: it does not grow, it does not glow, and it is the
 * only thing on screen that moves during the drag. The ghost slot on the grid does the talking
 * about WHERE it will land, which is why this is allowed to be purely physical.
 *
 * A CLONE, off-screen, removed on the next frame. The browser snapshots the node synchronously
 * during `setDragImage`, so one frame in the document is enough and the owner never sees it. The
 * clone is wrapped in a padded, transparent shell because a drag image is clipped to the
 * element's own box — an unpadded element loses its shadow to that clip entirely.
 *
 * Under `prefers-reduced-motion` the tilt is dropped and the native drag image stands: a chip
 * rotated for the whole length of a drag is exactly the sustained off-axis motion that setting
 * exists to refuse.
 */
function tiltedDragImage(event: React.DragEvent<HTMLElement>) {
  if (prefersReducedMotion() || typeof event.dataTransfer.setDragImage !== 'function') return
  const source = event.currentTarget
  const box = source.getBoundingClientRect()

  const shell = document.createElement('div')
  shell.style.cssText =
    'position:fixed;top:-1000px;left:-1000px;padding:14px;pointer-events:none;background:transparent'

  const clone = source.cloneNode(true) as HTMLElement
  clone.style.width = `${box.width}px`
  clone.style.margin = '0'
  clone.style.transform = 'rotate(-1.5deg)'
  clone.style.boxShadow = 'var(--shadow-drag-chip)'
  clone.style.background = 'var(--color-surface)'

  shell.appendChild(clone)
  document.body.appendChild(shell)
  event.dataTransfer.setDragImage(
    shell,
    event.clientX - box.left + 14,
    event.clientY - box.top + 14,
  )
  requestAnimationFrame(() => shell.remove())
}

// ============================================================================
// The day view's 300px right rail — docs/design/calendar-brief.md → Day view extras.
//
// Three cards, and each of them is a place the screen could lie:
//   · UNSCHEDULED is real open tasks with no time held for them, and its empty state says
//     "Nothing unscheduled" rather than backfilling with things already done.
//   · WHERE TODAY GOES states its denominator out loud and subtracts the remainder. It never
//     estimates unscheduled time (rule 3).
//   · The FOCUS CARD carries Canvas facts and only Canvas facts. Where Canvas supplies nothing —
//     a late policy, a time — it says so, because hidden and missing are different things.
// ============================================================================

const CARD =
  'flex flex-none flex-col rounded-panel border border-line-2 bg-surface px-[15px] py-[14px] shadow-calendar'

/**
 * How many unscheduled rows the card shows before it stops and says how many are left.
 *
 * The list is a DRAG SOURCE, not the Tasks board: twenty-five rows in a 300px rail push the two
 * cards below it off the screen, and the two cards below it are the ones that tell the truth
 * about the day. The count in the header is the REAL total either way, so nothing here hides how
 * much there is — it only declines to render all of it in a place it does not fit.
 */
const RAIL_ROWS = 6

const BAR_TONE = {
  due: 'bg-cal-due-rule',
  canvas: 'bg-cal-canvas-rule',
  neutral: 'bg-dash',
} as const

export function UnscheduledCard({
  tasks,
  today,
  onDragStart,
  onDragEnd,
}: {
  tasks: Task[]
  today: ISODate
  onDragStart: (task: Task) => void
  onDragEnd: () => void
}) {
  return (
    <div className={`${CARD} gap-[11px]`}>
      <div className="flex items-center gap-[9px]">
        <span className="text-[12px] font-semibold leading-none text-ink">Unscheduled</span>
        <span className="text-[10.5px] font-normal leading-none text-microcopy">
          from Reminders · {tasks.length} open
        </span>
        {tasks.length > 0 && (
          <span className="ml-auto text-[10px] font-semibold leading-none text-muted">Drag →</span>
        )}
      </div>

      {tasks.length === 0 ? (
        // The brief's exact string, and its exact instruction: do not backfill with completed
        // items. An empty list here means the board is clear, which is a thing worth saying.
        <div className="rounded-button border border-dashed border-dash px-[11px] py-[14px] text-center text-[11px] font-normal leading-none text-microcopy">
          Nothing unscheduled
        </div>
      ) : (
        <div className="flex flex-col gap-[6px]">
          {tasks.slice(0, RAIL_ROWS).map((task) => {
            const line = taskLine(task, today)
            const urgent = line.tone === 'due'
            return (
              <div
                key={task.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'copy'
                  e.dataTransfer.setData('text/plain', task.id)
                  // 13d · THE CHIP TILTS WHILE IT TRAVELS. Native drag-and-drop rasterises its
                  // image at `dragstart` from the element as it is right now, so styling the row
                  // through React state arrives a frame too late to appear in the picture. A
                  // styled clone handed to `setDragImage` is the only way the tilt and the
                  // 0 8px 20px shadow actually travel with the chip. Best-effort: a browser that
                  // refuses the custom image still drags the row's own snapshot.
                  tiltedDragImage(e)
                  onDragStart(task)
                }}
                onDragEnd={onDragEnd}
                title={
                  task.estimateMin === null
                    ? 'No estimate on this task, so dropping it blocks the default 30 minutes — and the calendar will never suggest a slot for it on its own.'
                    : `Drag onto the grid to hold ${formatDuration(task.estimateMin)} for it.`
                }
                className={[
                  'flex cursor-grab items-center gap-[9px] rounded-button border px-[11px] py-[9px]',
                  urgent ? 'border-cal-urgent-line bg-cal-urgent-fill' : 'border-line',
                ].join(' ')}
              >
                <span
                  className={['h-[22px] w-[5px] flex-none rounded-[3px]', BAR_TONE[line.tone]].join(' ')}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11.5px] font-semibold leading-[1.3] text-ink">
                    {task.title}
                  </span>
                  <span
                    className={[
                      'block text-[10px] font-normal leading-[1.4]',
                      urgent ? 'text-cal-due-ink' : 'text-muted',
                    ].join(' ')}
                  >
                    {line.text}
                  </span>
                </span>
                <Grip className="flex-none text-faintest-2" />
              </div>
            )
          })}

          {tasks.length > RAIL_ROWS && (
            <Link
              to="/tasks"
              className="px-[11px] py-1 text-[10px] font-medium leading-[1.4] text-microcopy underline underline-offset-2 hover:text-ink"
            >
              {tasks.length - RAIL_ROWS} more open on the Reminders board
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export function WhereTodayGoesCard({ blocks }: { blocks: CalendarBlock[] }) {
  const summary = summariseDay(blocks)

  return (
    <div className={`${CARD} gap-[10px]`}>
      <div className="text-[12px] font-semibold leading-none text-ink">Where today goes</div>

      <div className="flex h-2 overflow-hidden rounded-[5px] bg-cal-column">
        {summary.lanes.map((lane) => (
          <div
            key={lane.lane}
            className={LANE_BAR[lane.lane]}
            style={{ width: `${(lane.share * 100).toFixed(1)}%` }}
            aria-hidden
          />
        ))}
      </div>

      <div className="flex flex-col gap-[5px]">
        {summary.lanes.length === 0 ? (
          <div className="text-[10.5px] font-normal leading-none text-muted">
            Nothing is on today’s grid yet.
          </div>
        ) : (
          summary.lanes.map((lane) => (
            <div key={lane.lane} className="flex items-center gap-2">
              <span className={['size-[7px] flex-none rounded-[2px]', LANE_DOT[lane.lane]].join(' ')} aria-hidden />
              <span className="flex-1 text-[10.5px] font-normal leading-none text-ink-3">{lane.label}</span>
              <span className="text-[10.5px] font-semibold leading-none text-ink-2">
                {formatDuration(lane.minutes)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* RULE 3, SAID OUT LOUD. The denominator is named, the source of the numbers is named,
          and the remainder is subtraction — the card never claims to know what the rest holds.
          It says "the 15h DAY" rather than "the 15h SHOWN" since the grid grew to 24 hours:
          the drawn window and the counted one are no longer the same span, and the copy has to
          name the one this bar actually divides by. A block outside it is drawn, and is not in
          here — which is the honest reading of a bar that says 7 AM – 10 PM on its face. */}
      <div className="text-pretty text-[10px] font-normal leading-[1.5] text-microcopy">
        Share of the 15h day (7 AM – 10 PM), counted from blocks on the grid only. The other{' '}
        {formatDuration(summary.unscheduledMinutes)} is unscheduled — not estimated.
      </div>
    </div>
  )
}

export function FocusCard({
  deadline,
  today,
  nowMinutes,
  onBlockTime,
}: {
  deadline: CalendarDueItem | null
  today: ISODate
  nowMinutes: number
  /** The deadline, and the rect of the button that asked — the editor opens beside it. */
  onBlockTime: (deadline: CalendarDueItem, anchor: PeekAnchor) => void
}) {
  if (!deadline) {
    return (
      // `slab` and `on-dark`, not `ink` and `surface`: this card is dark ON PURPOSE in both
      // themes, and `ink` inverts to near-white while `surface` inverts to a dark card ground.
      // The dark lane split those tokens for exactly this case.
      <div className="flex flex-none flex-col gap-2 rounded-panel bg-slab px-[15px] py-[14px]">
        <div className="text-[11.5px] font-semibold leading-none text-on-dark">
          No open Canvas deadline
        </div>
        <div className="text-pretty text-[10.5px] font-normal leading-[1.55] text-cal-focus-ink-2">
          Nothing in your synced courses is still owed. If bCourses disagrees, the connector on
          Settings → Connections says when it last managed to look.
        </div>
      </div>
    )
  }

  const until = minutesUntil(deadline, today, nowMinutes)

  return (
    <div className="flex flex-none flex-col gap-2 rounded-panel bg-slab px-[15px] py-[14px]">
      <div className="text-[11.5px] font-semibold leading-none text-on-dark">
        {/* A countdown needs a moment. When Canvas gave a day and no time there is no moment,
            so the card names the day instead of manufacturing an hour to count down to. */}
        {until === null
          ? `${deadline.title} is due ${deadline.date === today ? 'today' : `on ${deadline.date}`}`
          : `${deadline.title} is due ${countdownPhrase(until)}`}
      </div>

      <div className="text-pretty text-[10.5px] font-normal leading-[1.55] text-cal-focus-ink-2">
        {deadline.courseCode ? `${deadline.courseCode}, from Canvas. ` : 'From Canvas. '}
        {deadline.hasTime && deadline.dueMinutes !== null
          ? `The submission window closes at ${compactTime(deadline.dueMinutes)}. `
          : 'Canvas gave no time for it, so LifeOS does not claim one. '}
        {/* HIDDEN ≠ MISSING. Canvas's assignment object carries no late policy, so the card says
            that rather than leaving a silence that reads as "there isn't one". */}
        Canvas sends no late policy, so this card cannot tell you one.
      </div>

      <div className="mt-[2px] flex gap-[7px]">
        <button
          type="button"
          onClick={(e) => onBlockTime(deadline, anchorOfElement(e.currentTarget))}
          style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
          className="mo-ink mo-press rounded-context bg-on-dark px-[11px] py-[6px] text-[10px] font-semibold leading-none text-slab"
        >
          Block time for this
        </button>
        {deadline.courseSlug && (
          <Link
            to={`/school/${deadline.courseSlug}`}
            style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
            className="mo-ink mo-press rounded-context border border-cal-focus-line px-[11px] py-[6px] text-[10px] font-semibold leading-none text-faintest-2"
          >
            Open in Course
          </Link>
        )}
      </div>
    </div>
  )
}

/** The standing footnote under the rail. Exact copy from the mockup. */
export function RailFootnote() {
  return (
    <div className="mt-auto text-pretty px-[3px] text-[10px] font-normal leading-[1.5] text-microcopy">
      Canvas events are read-only. Blocks you create live in LifeOS and can be moved, resized, or
      deleted.
    </div>
  )
}
